const PAGE_MARK = /^(?:---|===|\*\*\*|\[page\s*break\]|page\s*break|pagebreak|<!--\s*pagebreak\s*-->)$/i;

export type LetterSize = 'small' | 'normal' | 'large';

export type LetterStyle = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  size: LetterSize;
};

export type LetterRun = LetterStyle & {
  text: string;
};

export type LetterListKind = 'text' | 'bullet' | 'number';

export type LetterLine = {
  kind: LetterListKind;
  marker: string;
  text: string;
};

export type LetterBlock =
  | { type: 'text'; lines: string[] }
  | { type: 'bullet'; items: string[] }
  | { type: 'number'; items: LetterLine[] };

type Emphasis = 'bold' | 'italic' | 'underline';
type MarkCounts = Record<Emphasis, number>;

const PLAIN: LetterStyle = { bold: false, italic: false, underline: false, size: 'normal' };
const TOKEN: Record<Emphasis, string> = {
  bold: '**',
  italic: '*',
  underline: '__',
};
const OPEN_ORDER: Emphasis[] = ['underline', 'italic', 'bold'];
const CLOSE_ORDER: Emphasis[] = ['bold', 'italic', 'underline'];
const SIZE_OPEN: Record<Exclude<LetterSize, 'normal'>, string> = { large: '{+', small: '{-' };
const SIZE_CLOSE: Record<Exclude<LetterSize, 'normal'>, string> = { large: '+}', small: '-}' };

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sameStyle(left: LetterStyle, right: LetterStyle): boolean {
  return left.bold === right.bold
    && left.italic === right.italic
    && left.underline === right.underline
    && left.size === right.size;
}

function isMarkStart(source: string, index: number): boolean {
  return source.startsWith('**', index)
    || source.startsWith('__', index)
    || source.startsWith('{+', index)
    || source.startsWith('+}', index)
    || source.startsWith('{-', index)
    || source.startsWith('-}', index)
    || source[index] === '*';
}

type MarkToken = { kind: 'text' | Emphasis | 'large-open' | 'large-close' | 'small-open' | 'small-close'; value: string };

function tokenizeMarks(source: string): MarkToken[] {
  const tokens: MarkToken[] = [];
  let index = 0;
  while (index < source.length) {
    if (source.startsWith('**', index)) {
      tokens.push({ kind: 'bold', value: '**' });
      index += 2;
      continue;
    }
    if (source.startsWith('__', index)) {
      tokens.push({ kind: 'underline', value: '__' });
      index += 2;
      continue;
    }
    if (source.startsWith('{+', index)) {
      tokens.push({ kind: 'large-open', value: '{+' });
      index += 2;
      continue;
    }
    if (source.startsWith('+}', index)) {
      tokens.push({ kind: 'large-close', value: '+}' });
      index += 2;
      continue;
    }
    if (source.startsWith('{-', index)) {
      tokens.push({ kind: 'small-open', value: '{-' });
      index += 2;
      continue;
    }
    if (source.startsWith('-}', index)) {
      tokens.push({ kind: 'small-close', value: '-}' });
      index += 2;
      continue;
    }
    if (source[index] === '*') {
      tokens.push({ kind: 'italic', value: '*' });
      index += 1;
      continue;
    }
    let end = index + 1;
    while (end < source.length && !isMarkStart(source, end)) end += 1;
    tokens.push({ kind: 'text', value: source.slice(index, end) });
    index = end;
  }
  return tokens;
}

function liveSizeIndexes(tokens: MarkToken[]): Set<number> {
  const stack: Array<{ index: number; kind: 'large-open' | 'small-open' }> = [];
  const live = new Set<number>();
  tokens.forEach((token, index) => {
    if (token.kind === 'large-open' || token.kind === 'small-open') {
      stack.push({ index, kind: token.kind });
      return;
    }
    if (token.kind !== 'large-close' && token.kind !== 'small-close') return;
    const want = token.kind === 'large-close' ? 'large-open' : 'small-open';
    for (let cursor = stack.length - 1; cursor >= 0; cursor -= 1) {
      if (stack[cursor].kind === want) {
        live.add(stack[cursor].index);
        live.add(index);
        stack.splice(cursor, 1);
        return;
      }
    }
  });
  return live;
}

/** Marks that are opened and closed on the same line. An unmatched mark stays as typed. */
export function parseLetterMarks(line: string): LetterRun[] {
  const tokens = tokenizeMarks(String(line ?? ''));
  const counts: MarkCounts = { bold: 0, italic: 0, underline: 0 };
  tokens.forEach((token) => {
    if (token.kind === 'bold' || token.kind === 'italic' || token.kind === 'underline') counts[token.kind] += 1;
  });
  const usable: MarkCounts = {
    bold: counts.bold - (counts.bold % 2),
    italic: counts.italic - (counts.italic % 2),
    underline: counts.underline - (counts.underline % 2),
  };
  const seen: MarkCounts = { bold: 0, italic: 0, underline: 0 };
  const liveSizes = liveSizeIndexes(tokens);
  const open: LetterStyle = { ...PLAIN };
  const sizeStack: LetterSize[] = ['normal'];
  const runs: LetterRun[] = [];
  let buffer = '';
  const flush = () => {
    if (!buffer) return;
    const previous = runs[runs.length - 1];
    if (previous && sameStyle(previous, open)) previous.text += buffer;
    else runs.push({ text: buffer, ...open });
    buffer = '';
  };

  tokens.forEach((token, index) => {
    if (token.kind === 'text') {
      buffer += token.value;
      return;
    }
    if (token.kind === 'bold' || token.kind === 'italic' || token.kind === 'underline') {
      seen[token.kind] += 1;
      if (seen[token.kind] > usable[token.kind]) {
        buffer += token.value;
        return;
      }
      flush();
      open[token.kind] = !open[token.kind];
      return;
    }
    if (!liveSizes.has(index)) {
      buffer += token.value;
      return;
    }
    flush();
    if (token.kind === 'large-open') sizeStack.push('large');
    else if (token.kind === 'small-open') sizeStack.push('small');
    else {
      const removing = token.kind === 'large-close' ? 'large' : 'small';
      const at = sizeStack.lastIndexOf(removing);
      if (at > 0) sizeStack.splice(at, 1);
    }
    open.size = sizeStack[sizeStack.length - 1];
  });
  flush();
  return runs;
}

/** A leading "- " is a bullet. A leading "1. " is a numbered line. */
export function readLetterLine(line: string): LetterLine {
  const bullet = /^- (.*)$/.exec(line);
  if (bullet) return { kind: 'bullet', marker: '', text: bullet[1] };
  const number = /^(\d+)\. (.*)$/.exec(line);
  if (number) return { kind: 'number', marker: number[1], text: number[2] };
  return { kind: 'text', marker: '', text: line };
}

export function letterBlocks(lines: string[]): LetterBlock[] {
  const blocks: LetterBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const current = readLetterLine(lines[index]);
    if (current.kind === 'text') {
      const group: string[] = [];
      while (index < lines.length && readLetterLine(lines[index]).kind === 'text') {
        group.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: 'text', lines: group });
      continue;
    }
    if (current.kind === 'bullet') {
      const items: string[] = [];
      while (index < lines.length && readLetterLine(lines[index]).kind === 'bullet') {
        items.push(readLetterLine(lines[index]).text);
        index += 1;
      }
      blocks.push({ type: 'bullet', items });
      continue;
    }
    const items: LetterLine[] = [];
    while (index < lines.length && readLetterLine(lines[index]).kind === 'number') {
      items.push(readLetterLine(lines[index]));
      index += 1;
    }
    blocks.push({ type: 'number', items });
  }
  return blocks;
}

export function letterPlainText(value: string): string {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => {
      if (!line || PAGE_MARK.test(line.trim())) return line;
      const read = readLetterLine(line);
      const words = parseLetterMarks(read.text).map((run) => run.text).join('');
      if (read.kind === 'bullet') return words ? `- ${words}` : '-';
      if (read.kind === 'number') return words ? `${read.marker}. ${words}` : `${read.marker}.`;
      return words;
    })
    .join('\n');
}

function sizeHtml(html: string, size: LetterSize): string {
  if (!html || size === 'normal') return html;
  const scale = size === 'large' ? '1.35em' : '0.8em';
  return `<span class="letter-size-${size}" style="font-size: ${scale};">${html}</span>`;
}

export function letterLineHtml(line: string): string {
  return parseLetterMarks(line).map((run) => {
    let html = escapeHtml(run.text);
    if (!html) return '';
    if (run.underline) html = `<u style="text-decoration: underline;">${html}</u>`;
    if (run.italic) html = `<em style="font-style: italic;">${html}</em>`;
    if (run.bold) html = `<strong style="font-weight: 600;">${html}</strong>`;
    return sizeHtml(html, run.size);
  }).join('');
}

function lineEditorHtml(line: string): string {
  const read = readLetterLine(line);
  return letterLineHtml(read.kind === 'text' ? line : read.text) || '<br>';
}

/** The writing surface: one div per line, with lists and only the marks the letter allows. */
export function letterToEditorHtml(value: string): string {
  const normalized = String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized) return '';
  const lines = normalized.split('\n');
  const html: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const read = readLetterLine(lines[index]);
    if (read.kind === 'text') {
      html.push(`<div>${lineEditorHtml(lines[index])}</div>`);
      index += 1;
      continue;
    }
    const kind = read.kind;
    const items: string[] = [];
    while (index < lines.length && readLetterLine(lines[index]).kind === kind) {
      const item = readLetterLine(lines[index]);
      const value = kind === 'number' ? ` value="${escapeHtml(item.marker)}"` : '';
      items.push(`<li${value}>${lineEditorHtml(lines[index])}</li>`);
      index += 1;
    }
    html.push(kind === 'bullet' ? `<ul>${items.join('')}</ul>` : `<ol>${items.join('')}</ol>`);
  }
  return html.join('');
}

function styleTransition(current: LetterStyle, next: LetterStyle): string {
  let marks = '';
  CLOSE_ORDER.forEach((key) => {
    if (current[key] && !next[key]) marks += TOKEN[key];
  });
  if (current.size !== 'normal' && current.size !== next.size) marks += SIZE_CLOSE[current.size];
  if (next.size !== 'normal' && current.size !== next.size) marks += SIZE_OPEN[next.size];
  OPEN_ORDER.forEach((key) => {
    if (!current[key] && next[key]) marks += TOKEN[key];
  });
  return marks;
}

function styleOf(node: Node, root: HTMLElement): LetterStyle {
  const style = { ...PLAIN };
  let size: LetterSize | null = null;
  let current = node.parentElement;
  while (current && current !== root) {
    const tag = current.tagName;
    if (tag === 'STRONG' || tag === 'B') style.bold = true;
    if (tag === 'EM' || tag === 'I') style.italic = true;
    if (tag === 'U') style.underline = true;
    if (!size && current.classList.contains('letter-size-large')) size = 'large';
    if (!size && current.classList.contains('letter-size-small')) size = 'small';
    if (!size && tag === 'FONT') {
      const fontSize = Number(current.getAttribute('size') || '');
      if (fontSize > 0 && fontSize < 3) size = 'small';
      if (fontSize >= 5) size = 'large';
    }
    current = current.parentElement;
  }
  style.size = size || 'normal';
  return style;
}

/** Reads the writing surface back into the stored letter, including page breaks. */
export function serializeLetterHtml(root: HTMLElement): string {
  const lines: string[] = [];
  let parts: string[] = [];
  let style: LetterStyle = { ...PLAIN };

  const hasPending = () => parts.length > 0 || !sameStyle(style, PLAIN);

  const endLine = () => {
    if (!sameStyle(style, PLAIN)) parts.push(styleTransition(style, PLAIN));
    style = { ...PLAIN };
    lines.push(parts.join(''));
    parts = [];
  };

  const breakLine = () => {
    if (hasPending()) endLine();
    else lines.push('');
  };

  const appendText = (text: string, next: LetterStyle) => {
    const chunks = text.replace(/\u200b/g, '').split('\n');
    chunks.forEach((chunk, chunkIndex) => {
      if (chunkIndex > 0) breakLine();
      if (!chunk) return;
      parts.push(styleTransition(style, next) + chunk);
      style = { ...next };
    });
  };

  const visitInline = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendText((node.textContent || '').replace(/\u00a0/g, ' '), styleOf(node, root));
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === 'BR') {
      breakLine();
      return;
    }
    Array.from(node.childNodes).forEach(visitInline);
  };

  const collectItem = (item: HTMLElement): string => {
    const savedParts = parts;
    const savedStyle = style;
    parts = [];
    style = { ...PLAIN };
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        appendText((node.textContent || '').replace(/\u00a0/g, ' ').replace(/\n/g, ' '), styleOf(node, root));
        return;
      }
      if (!(node instanceof HTMLElement)) return;
      if (node.tagName === 'BR') {
        appendText(' ', styleOf(node, root));
        return;
      }
      Array.from(node.childNodes).forEach(walk);
    };
    Array.from(item.childNodes).forEach(walk);
    if (!sameStyle(style, PLAIN)) parts.push(styleTransition(style, PLAIN));
    const body = parts.join('').replace(/[ \t]+$/g, '');
    parts = savedParts;
    style = savedStyle;
    return body;
  };

  const visitList = (list: HTMLElement) => {
    if (hasPending()) endLine();
    let nextNumber = 1;
    Array.from(list.children).forEach((child) => {
      if (!(child instanceof HTMLElement) || child.tagName !== 'LI') return;
      const body = collectItem(child).trim();
      if (list.tagName === 'OL') {
        const explicit = Number(child.getAttribute('value'));
        const number = Number.isFinite(explicit) && explicit > 0 ? explicit : nextNumber;
        nextNumber = number + 1;
        lines.push(body ? `${number}. ${body}` : `${number}. `);
        return;
      }
      lines.push(body ? `- ${body}` : '- ');
    });
  };

  const visitBlock = (node: Node) => {
    if (node instanceof HTMLElement && (node.tagName === 'UL' || node.tagName === 'OL')) {
      visitList(node);
      return;
    }
    if (node instanceof HTMLElement && (node.tagName === 'DIV' || node.tagName === 'P')) {
      if (hasPending()) endLine();
      const onlyBreak = node.childNodes.length === 1
        && node.firstChild instanceof HTMLElement
        && node.firstChild.tagName === 'BR';
      if (onlyBreak || node.childNodes.length === 0) {
        lines.push('');
        return;
      }
      Array.from(node.childNodes).forEach(visitInline);
      if (hasPending()) endLine();
      return;
    }
    if (node instanceof HTMLElement && node.tagName === 'BR') {
      breakLine();
      return;
    }
    visitInline(node);
  };

  Array.from(root.childNodes).forEach(visitBlock);
  if (hasPending()) endLine();
  if (lines.length === 1 && lines[0] === '') return '';
  return lines.join('\n');
}

/** Pages, then paragraphs, then the lines inside each paragraph. */
export function parseQuoteLetter(value: string): string[][][] {
  const normalized = String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u000c/g, '\n\u000c\n');
  if (!normalized.trim()) return [];

  const pages: string[][][] = [];
  let paragraphs: string[][] = [];
  let lines: string[] = [];
  let blankRun = 0;

  const endParagraph = () => {
    if (lines.length) paragraphs.push(lines);
    lines = [];
  };
  const endPage = () => {
    endParagraph();
    if (paragraphs.length) pages.push(paragraphs);
    paragraphs = [];
    blankRun = 0;
  };

  normalized.split('\n').forEach((raw) => {
    if (raw.includes('\u000c') || PAGE_MARK.test(raw.trim())) {
      endPage();
      return;
    }
    if (!raw.trim()) {
      if (lines.length) {
        endParagraph();
        blankRun = 1;
      } else if (paragraphs.length && blankRun > 0) {
        blankRun += 1;
        if (blankRun >= 2) endPage();
      }
      return;
    }
    blankRun = 0;
    lines.push(raw.trimEnd());
  });
  endPage();
  return pages;
}

const PARAGRAPH_STYLE = 'margin: 0 0 1em; font-size: 16px; line-height: 1.6; color: #333; white-space: pre-wrap;';
const LIST_STYLE = 'margin: 0 0 1em; padding-left: 1.3em; font-size: 16px; line-height: 1.6; color: #333;';

function blocksHtml(lines: string[]): string {
  return letterBlocks(lines).map((block) => {
    if (block.type === 'bullet') {
      const items = block.items.map((item) => `<li style="margin: 0 0 0.25em;">${letterLineHtml(item) || '&nbsp;'}</li>`).join('');
      return `<ul style="${LIST_STYLE}">${items}</ul>`;
    }
    if (block.type === 'number') {
      const items = block.items.map((item) => (
        `<li value="${escapeHtml(item.marker)}" style="margin: 0 0 0.25em;">${letterLineHtml(item.text) || '&nbsp;'}</li>`
      )).join('');
      return `<ol style="${LIST_STYLE}">${items}</ol>`;
    }
    return `<p style="${PARAGRAPH_STYLE}">${block.lines.map((line) => letterLineHtml(line)).join('<br>')}</p>`;
  }).join('');
}

export function quoteLetterHtml(value: string): string {
  return parseQuoteLetter(value).map((page, pageIndex) => {
    const paragraphs = page.map((paragraph) => blocksHtml(paragraph)).join('');
    const rule = pageIndex > 0
      ? 'page-break-before: always; break-before: page; margin-top: 36px; padding-top: 28px; border-top: 1px solid #e4d8c4;'
      : '';
    return `<div class="letter-page" style="${rule}">${paragraphs}</div>`;
  }).join('');
}
